import * as React from 'react';
import * as formatParse from 'common/format-parse';
import { observer } from 'mobx-react';
import { Amount as AmountBusinessLogic } from 'nomst/Stores/Decision/amount';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { Input, FormGroup } from '@payscale/design';
import { ICountryDecisionDispatcher } from 'typings/countryDecision.dispatcher';
import EligibilityTooltip from 'components/Common/eligibilityTooltip';
import SafeMath from 'common/safeMath';
import { AmountCode, AmountPayType } from 'typings/enums';
import AmountConvert from 'common/amountConvert';
import { AmountOutOfBoundsModal } from './amountOutOfBoundsModal';
import './amount.scss';

export interface IAmountProps {
    employeeSourceId: string;
    amount: AmountBusinessLogic;
    title: string;
    configuration: Configuration;
    countryDecisionDispatcher: Nullable<ICountryDecisionDispatcher>;
    isHourly: boolean;
    annualStandardWorkHours: number;
    fullTimeEquivalent: number;
    isReadOnly: boolean;
    idSuffix: string;
}

export interface IAmountState {
    amountText: string;
    percentageText: string;
    isAmountOnFocus: boolean;
    isPercentageOnFocus: boolean;
    amountValueBeingUpdated: boolean;
    isModalOpen: boolean;
}

export class Amount extends React.Component<IAmountProps, IAmountState> {

    constructor(props: IAmountProps) {
        super(props);
        this.state = {
            amountText: '',
            percentageText: '',
            isAmountOnFocus: false,
            isPercentageOnFocus: false,
            amountValueBeingUpdated: false,
            isModalOpen: false
        };

        this.onChangePercentage = this.onChangePercentage.bind(this);
        this.onBlurPercentage = this.onBlurPercentage.bind(this);
        this.onFocusPercentage = this.onFocusPercentage.bind(this);
        this.onChangeAmount = this.onChangeAmount.bind(this);
        this.onBlurAmount = this.onBlurAmount.bind(this);
        this.onFocusAmount = this.onFocusAmount.bind(this);
        this.handleModalOpen = this.handleModalOpen.bind(this);
        this.handleModalClose = this.handleModalClose.bind(this);
    }

    render(): React.ReactNode {
        if (!this.props.amount) {
            return null;
        }
        return (
            <div className="amount">
                {this.renderAmountForm()}
                {this.renderCycleData()}
                {this.renderOutOfBoundsModal()}
            </div>
        );
    }

    private onChangePercentage(_: undefined, value: string): void {
        const percentage = formatParse.parseValueOrDefault(value, 0);
        this.setPercentage(percentage);
        this.setState(() => ({
            percentageText: value,
            amountValueBeingUpdated: true
        } as IAmountState));
    }

    private onBlurPercentage(): void {
        const {
            amount,
            employeeSourceId,
            countryDecisionDispatcher } = this.props;
        if (this.state.amountValueBeingUpdated) {
            const percentage = formatParse.parseValueOrDefault(this.state.percentageText, 0);
            this.setPercentage(percentage);
            if (!amount.isInBounds) {
                this.handleModalOpen();
            }
            if (countryDecisionDispatcher) {
                countryDecisionDispatcher.dispatchAmountUpdated(
                    employeeSourceId,
                    amount.amountCode);
            }
        }

        this.setState(() => ({
            isPercentageOnFocus: false,
            amountValueBeingUpdated: false
        } as IAmountState));
    }

    private onChangeAmount(_: undefined, value: string): void {
        const amountValue = formatParse.parseValueOrDefault(value, 0);
        this.setAmount(amountValue);
        this.setState(() => ({
            amountText: value,
            amountValueBeingUpdated: true
        } as IAmountState));
    }

    private onBlurAmount(): void {
        const {
            amount,
            employeeSourceId,
            countryDecisionDispatcher } = this.props;
        if (this.state.amountValueBeingUpdated) {
            const amountValue = formatParse.parseValueOrDefault(this.state.amountText, 0);
            this.setAmount(amountValue);
            if (!amount.isInBounds) {
                this.handleModalOpen();
            }
            if (countryDecisionDispatcher) {
                countryDecisionDispatcher.dispatchAmountUpdated(
                    employeeSourceId,
                    amount.amountCode);
            }
        }

        this.setState(() => ({
            isAmountOnFocus: false,
            amountValueBeingUpdated: false
        } as IAmountState));
    }

    private onFocusPercentage(): void {
        const { configuration } = this.props;
        const text = configuration.format.formatPercentage(this.percentage);
        this.setState(() => ({
            isPercentageOnFocus: true,
            percentageText: text,
            amountValueBeingUpdated: false
        } as IAmountState));
    }

    private onFocusAmount(): void {
        const { isHourly, amount, configuration } = this.props;
        const text = configuration.format.formatCurrencyValue(
            amount.currentIsoCurrencyCode,
            amount.convertedAmount,
            isHourly);

        this.setState(() => ({
            isAmountOnFocus: true,
            amountText: text,
            amountValueBeingUpdated: false
        } as IAmountState));
    }

    private renderAmountForm(): React.ReactNode {
        const { amount, title, isReadOnly } = this.props;
        if (amount.isEligible === false) {
            return (
                <div className="amount-full-width-container">
                    <div>
                        <p className="amount-title">{title}</p>
                    </div>
                </div>
            );
        }

        if (isReadOnly) {
            return this.renderReadOnlyForm();
        }

        return this.renderEditableForm();
    }

    private renderEditableForm(): React.ReactNode {
        const { amount, title, employeeSourceId, idSuffix: idSuffix } = this.props;
        const percentLabel = `amount-${title}-percentage-${employeeSourceId}-${idSuffix}`;
        const amountLabel = `amount-${title}-${employeeSourceId}-${idSuffix}`;
        return (
            <div className="editable-amount">
                <div className="editable-amount__form" hidden={!amount.canUserModifyPercentage}>
                    <label htmlFor={percentLabel} className="amount-title">{title} %</label>
                    <div className="input-label-align">
                        <FormGroup className="amount-form">
                            <Input
                                id={percentLabel}
                                data-mf-replace="***"
                                value={this.percentageText}
                                onChange={this.onChangePercentage}
                                onBlur={this.onBlurPercentage}
                                onFocus={this.onFocusPercentage}
                                className={this.getAmountClassName(amount.amountCode, 'percentage')}
                            />
                        </FormGroup>
                    </div>
                </div>
                <div className="editable-amount__form">
                    <label htmlFor={amountLabel} className="amount-title">{title}</label>
                    <div className="input-label-align">
                        <FormGroup className="amount-form">
                            <Input
                                id={amountLabel}
                                data-mf-replace="***"
                                value={this.amountText}
                                onChange={this.onChangeAmount}
                                onBlur={this.onBlurAmount}
                                onFocus={this.onFocusAmount}
                                className={this.getAmountClassName(amount.amountCode, 'value')}
                            />
                        </FormGroup>
                        {this.renderHourlyLabel()}
                    </div>
                </div>
            </div>
        );
    }

    private renderReadOnlyForm(): React.ReactNode {
        const { title } = this.props;
        return (
            <div className="readonly-amount-form">
                <p className="amount-title">{title}</p>
                {this.renderReadOnlyFormText()}
            </div>
        );
    }

    private renderReadOnlyFormText(): React.ReactNode {
        const { isHourly, amount, configuration } = this.props;
        return (
            <p className="amount-readonly-text" data-mf-replace="***">
                {configuration.format.formatPercentage(this.percentage)}
                <span> / </span>
                {configuration.format.formatCurrencyValue(
                    amount.currentIsoCurrencyCode,
                    amount.convertedAmount,
                    isHourly)}
                {this.renderHourlyLabel()}
            </p>
        );
    }

    private renderHourlyLabel(): React.ReactNode {
        const { isHourly, amount } = this.props;
        if (!isHourly
            || amount.amountCode === AmountCode.LumpSum
            || amount.amountCode === AmountCode.Bonus
            || amount.amountCode === AmountCode.VariablePay) {
            return null;
        }

        return (
            <span className="amount__hourly-label">&#47;hour</span>
        );
    }

    private renderCycleData(): React.ReactNode {
        const { amount } = this.props;
        if (amount.isEligible === false) {
            return this.renderNotEligible();
        } else {
            return (
                <React.Fragment>
                    {this.renderTargetText()}
                    {this.renderProration()}
                </React.Fragment>
            );
        }
    }

    private renderNotEligible(): React.ReactNode {
        const { amount, configuration, employeeSourceId, idSuffix: idSuffix } = this.props;
        return (
            <EligibilityTooltip
                id={`${amount.amountCode}-${employeeSourceId}-${idSuffix}`}
                text={configuration.getEligibilityRuleNote(amount.amountCode)}
            />
        );
    }

    private renderTargetText(): React.ReactNode {
        const { amount, configuration } = this.props;
        if (amount.amountCode === AmountCode.Merit && configuration.displayIncreaseRecommendation) {
            return this.renderMeritRecommendation();
        } else if (amount.amountCode === AmountCode.Bonus) {
            return this.renderTarget('Bonus');
        } else if (amount.amountCode === AmountCode.VariablePay) {
            return this.renderTarget('Variable Pay');
        }
        return null;
    }

    private renderMeritRecommendation(): React.ReactNode {
        const { amount, configuration } = this.props;
        if (!amount || !amount.targetAmount || !amount.currentPay) {
            return null;
        }
        const percentageDecimal = SafeMath.divide(amount.targetAmount, amount.currentPay);
        const percentageText = configuration.format.formatDecimalToPercentage(percentageDecimal);
        return (
            <div className="suggested-merit-recommendation">
                <span>Suggested&#32;<strong data-mf-replace="***">{percentageText}</strong></span>
            </div>
        );
    }

    private renderTarget(targetTitle: string): React.ReactNode {
        const { amount, configuration, annualStandardWorkHours, isHourly, fullTimeEquivalent } = this.props;
        const targetAmountText = configuration.format.formatCurrencyValue(
            amount.currentIsoCurrencyCode,
            amount.convertedTarget,
            false);
        const targetPercentageDecimal = amount.getTargetPercentageAsDecimal(
            annualStandardWorkHours,
            isHourly,
            fullTimeEquivalent);
        const targetPercentageText = configuration.format.formatDecimalToPercentage(targetPercentageDecimal);
        return (
            <span className="target">
                {targetTitle} Target&#32;
                <strong data-mf-replace="***">{targetAmountText}</strong>&#32;({targetPercentageText} of current pay)
            </span>
        );
    }

    private renderProration(): React.ReactNode {
        const { amount, configuration } = this.props;
        if (amount.isProrated === false) {
            return null;
        }

        const proratedPercentage = this.percentage * amount.prorateRatio;
        const proratedPercentageText = configuration.format.formatPercentage(proratedPercentage);
        return (
            <div className="proration-label">
                <span>
                    Proration&#32;
                    <strong data-mf-replace="***">
                        {proratedPercentageText}
                    </strong>
                </span>
            </div>
        );
    }

    private renderOutOfBoundsModal(): React.ReactNode {
        const { configuration } = this.props;
        if (!this.state.isModalOpen) {
            return null;
        }

        return (
            <AmountOutOfBoundsModal
                lowerBound={configuration.managerIncreasePercentageUnder}
                upperBound={configuration.managerIncreasePercentageOver}
                onCloseClick={this.handleModalClose}
            />
        );
    }

    private get percentageText(): string {
        const { configuration } = this.props;

        if (this.state.percentageText === null) {
            return '';
        }

        if (this.state.isPercentageOnFocus) {
            return this.state.percentageText;
        }

        return configuration.format.formatPercentage(this.percentage);
    }

    private get amountText(): string {
        const { isHourly, amount, configuration } = this.props;

        if (this.state.amountText === null) {
            return '';
        }

        if (this.state.isAmountOnFocus) {
            return this.state.amountText;
        }

        return configuration.format.formatCurrencyValue(
            amount.currentIsoCurrencyCode, amount.convertedAmount, isHourly);
    }

    private get percentage(): number {
        const { amount } = this.props;
        return amount.payType !== AmountPayType.BasePay
            ? amount.getPercentageBasedOnAnnualPay(this.annualCurrentPay)
            : amount.percentage;
    }

    private get annualCurrentPay(): number {
        const { amount, annualStandardWorkHours, isHourly, fullTimeEquivalent } = this.props;
        return AmountConvert.convertToAnnual(
            amount.currentPay,
            annualStandardWorkHours,
            isHourly,
            fullTimeEquivalent);
    }

    private setAmount(value: number): void {
        const {
            amount,
            countryDecisionDispatcher,
            isHourly,
            annualStandardWorkHours,
            fullTimeEquivalent
        } = this.props;

        const previousAmountValue = amount.value;
        amount.setValue(value);
        const currentAmountValue = amount.value;
        if (!countryDecisionDispatcher) {
            return;
        }
        countryDecisionDispatcher.dispatchAmountChanged(
            amount.amountCode,
            amount.amountIsoCurrencyCode,
            previousAmountValue,
            currentAmountValue,
            amount.updateNewPay,
            isHourly,
            annualStandardWorkHours,
            fullTimeEquivalent);
    }

    private setPercentage(percentage: number): void {
        const {
            amount,
            countryDecisionDispatcher,
            isHourly,
            annualStandardWorkHours,
            fullTimeEquivalent
        } = this.props;

        const previousAmountValue = amount.value;
        let annualCurrentPay = null;
        if (amount.payType !== AmountPayType.BasePay) {
            annualCurrentPay = this.annualCurrentPay;
        }

        amount.setPercentage(percentage, true, annualCurrentPay);
        const currentAmountValue = amount.value;
        if (!countryDecisionDispatcher) {
            return;
        }

        countryDecisionDispatcher.dispatchAmountChanged(
            amount.amountCode,
            amount.amountIsoCurrencyCode,
            previousAmountValue,
            currentAmountValue,
            amount.updateNewPay,
            isHourly,
            annualStandardWorkHours,
            fullTimeEquivalent);
    }

    private getAmountClassName(amountCode: AmountCode, modifier: string): string {
        return `amount__${amountCode}-${modifier}`;
    }

    private handleModalOpen(): void {
        this.setState({ isModalOpen: true });
        this.setAmount(0);
    }

    private handleModalClose(): void {
        this.setState({ isModalOpen: false });
    }
}

export default observer(Amount);
